/**
 * CapacityBanner — sums of `quoted_labour_hours` across active job cards
 * (pickup → completed) in a configurable date range. Lives at the top of
 * the JC kanban so the SM can plan capacity at a glance.
 *
 * Defaults to the current ISO week (Mon→Sun). The user can flip to
 * Day / Week / Month, OR pick a custom range. Mirrors the JC date-jump
 * pattern so the SM never has to leave the kanban.
 */
import { useCallback, useEffect, useState } from 'react';
import { Clock, Calendar, ChevronDown, AlertTriangle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { withSessionHash } from '@/lib/authFetch';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'custom', label: 'Custom…' },
];

const fmtHours = (h) => {
  const n = Number(h) || 0;
  if (n === 0) return '0h';
  return Number.isInteger(n) ? `${n}h` : `${n.toFixed(1)}h`;
};

export default function CapacityBanner({ getSiteParams, onPeriodSync }) {
  // iter128 — SM asked for the default period to land on DAY so the
  // kanban opens to "what's on today" rather than the rolling weekly
  // view. The setting only impacts the *initial* render; the SM can
  // still flip to Week / Month / Custom anytime via the selector.
  const [period, setPeriod] = useState('day');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  // iter193 — per-mechanic completed-hours drill-down (tap a row to audit
  // exactly which completed tasks make up the number).
  const [expandedMech, setExpandedMech] = useState(null);
  const [mechData, setMechData] = useState({});
  const [mechLoading, setMechLoading] = useState(false);
  // iter198 — "Allocated Hours" (labour sold in period) audit drill-down.
  const [allocExpanded, setAllocExpanded] = useState(false);
  const [allocData, setAllocData] = useState(null);
  const [allocLoading, setAllocLoading] = useState(false);
  // iter223 — Billed Hours audit (per-invoice line breakdown).
  const [billedExpanded, setBilledExpanded] = useState(false);
  const [billedData, setBilledData] = useState(null);
  const [billedLoading, setBilledLoading] = useState(false);

  // Push the current period/range to the parent so the kanban below can
  // mirror this selection. Previously the banner's period and the kanban's
  // view-type were independent — the SM could pick "Custom Jan→Jun" here
  // and not realise the kanban was still showing today's view.
  useEffect(() => {
    if (!onPeriodSync) return;
    if (period === 'custom') {
      if (customStart && customEnd) onPeriodSync('custom', { start: customStart, end: customEnd });
    } else {
      onPeriodSync(period, null);
    }
  }, [period, customStart, customEnd, onPeriodSync]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const siteParams = getSiteParams ? getSiteParams() : '';
      let qs = `period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        qs += `&start_date=${customStart}&end_date=${customEnd}`;
      } else if (period === 'custom') {
        // Don't fire while the SM hasn't completed the range yet.
        setLoading(false);
        return;
      }
      const sep = siteParams ? '&' : '?';
      const url = `${BACKEND_URL}/api/job-cards/capacity-summary?${qs}${siteParams ? sep + siteParams.replace(/^[?&]/, '') : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (_e) {
      // ignore — empty state UI handles it.
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd, getSiteParams]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // iter193 — fetch one mechanic's completed-task breakdown on demand.
  const fetchMechBreakdown = useCallback(async (userId) => {
    setMechLoading(true);
    try {
      const siteParams = getSiteParams ? getSiteParams() : '';
      let qs = `period=${period}&mechanic_id=${encodeURIComponent(userId)}`;
      if (period === 'custom' && customStart && customEnd) {
        qs += `&start_date=${customStart}&end_date=${customEnd}`;
      }
      const sep = siteParams ? '&' : '?';
      const url = `${BACKEND_URL}/api/job-cards/mechanic-completed-breakdown?${qs}${siteParams ? sep + siteParams.replace(/^[?&]/, '') : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setMechData(prev => ({ ...prev, [userId]: d }));
      }
    } catch (_e) {
      // ignore — empty-state UI handles it.
    } finally {
      setMechLoading(false);
    }
  }, [period, customStart, customEnd, getSiteParams]);

  const toggleMech = useCallback((userId) => {
    setExpandedMech(prev => {
      const next = prev === userId ? null : userId;
      if (next && !mechData[userId]) fetchMechBreakdown(userId);
      return next;
    });
  }, [mechData, fetchMechBreakdown]);

  // Reset the drill-down whenever the window changes (cached rows would be stale).
  useEffect(() => {
    setExpandedMech(null);
    setMechData({});
    setAllocData(null);
    setBilledData(null);
  }, [period, customStart, customEnd]);

  // iter198 — fetch the "Allocated Hours" audit (which tasks, on which cards,
  // added when) so the SM can see exactly what makes up the number.
  const fetchAllocBreakdown = useCallback(async () => {
    setAllocLoading(true);
    try {
      const siteParams = getSiteParams ? getSiteParams() : '';
      let qs = `period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        qs += `&start_date=${customStart}&end_date=${customEnd}`;
      } else if (period === 'custom') {
        setAllocLoading(false);
        return;
      }
      const sep = siteParams ? '&' : '?';
      const url = `${BACKEND_URL}/api/job-cards/allocated-breakdown?${qs}${siteParams ? sep + siteParams.replace(/^[?&]/, '') : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setAllocData(await res.json());
    } catch (_e) {
      // ignore — empty-state UI handles it.
    } finally {
      setAllocLoading(false);
    }
  }, [period, customStart, customEnd, getSiteParams]);

  const toggleAlloc = useCallback(() => setAllocExpanded(v => !v), []);

  // iter223 — Billed Hours audit fetch (mirrors fetchAllocBreakdown).
  const fetchBilledBreakdown = useCallback(async () => {
    setBilledLoading(true);
    try {
      const siteParams = getSiteParams ? getSiteParams() : '';
      let qs = `period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        qs += `&start_date=${customStart}&end_date=${customEnd}`;
      } else if (period === 'custom') {
        setBilledLoading(false);
        return;
      }
      const sep = siteParams ? '&' : '?';
      const url = `${BACKEND_URL}/api/job-cards/billed-breakdown?${qs}${siteParams ? sep + siteParams.replace(/^[?&]/, '') : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setBilledData(await res.json());
    } catch (_e) {
      // ignore — empty-state UI handles it.
    } finally {
      setBilledLoading(false);
    }
  }, [period, customStart, customEnd, getSiteParams]);

  const toggleBilled = useCallback(() => setBilledExpanded(v => !v), []);

  useEffect(() => {
    if (billedExpanded) fetchBilledBreakdown();
  }, [billedExpanded, fetchBilledBreakdown]);

  // Fetch when the panel opens, and refresh when the window changes while open
  // (fetchAllocBreakdown's identity changes with period/custom range).
  useEffect(() => {
    if (allocExpanded) fetchAllocBreakdown();
  }, [allocExpanded, fetchAllocBreakdown]);

  // Refresh every 60s so newly-created JCs surface without forcing the SM
  // to refresh. Cheap aggregation, single endpoint call.
  useEffect(() => {
    const t = setInterval(fetchSummary, 60_000);
    return () => clearInterval(t);
  }, [fetchSummary]);

  const total = data?.billed_hours ?? 0;
  // iter178 — TWO distinct allocated figures:
  //  (1) snapshotAllocated — LIVE snapshot of ALL active job cards' task
  //      hours right now; does NOT change with the Day/Week/Month toggle.
  //  (2) windowedAllocated — task hours for line_items CREATED within the
  //      selected window; DOES change with the toggle.
  const snapshotAllocated = data?.total_active_allocated_hours ?? 0;
  // iter182 — Windowed "Allocated Hours" = full quoted labour hours of ALL
  // tasks on job cards whose SERVICE DAY falls in the selected window,
  // INCLUDING job cards that were COMPLETED in the period (and their
  // completed tasks). Fixes: a car finished today was dropping out of the
  // day's allocated hours because the previous figure (`total_hours`) only
  // counted ACTIVE statuses.
  const windowedAllocated = data?.allocated_window_hours ?? data?.total_hours ?? 0;
  // By-stage breakdown denominator stays ACTIVE-only (it visualises active
  // kanban stages; `completed` isn't an active stage).
  const byStageTotal = data?.total_hours ?? 0;
  const jcCount = data?.billed_jc_count ?? 0;
  const byStatus = data?.billed_by_status || {};
  const byMechanic = data?.billed_by_mechanic || [];
  const unassigned = data?.allocated_window_unassigned_hours ?? data?.unassigned_hours ?? 0;
  // iter144 — Produced + Allocated hours per mechanic (from new
  // produced_by_mechanic array on /capacity-summary). Index by user_id
  // so the Show Breakdown row can render "produced / allocated" without
  // a second lookup.
  const producedByMechanic = data?.produced_by_mechanic || [];
  const producedIndex = {};
  for (const m of producedByMechanic) producedIndex[m.user_id] = m;
  // iter125: target / goal for the active period — Achieved vs Goal so
  // the SM knows whether the team is tracking ahead / on / behind.
  const targetHours = data?.target_hours ?? 0;
  const targetPct = data?.target_pct ?? 0;
  const targetGap = Math.max(targetHours - total, 0);
  const periodLabel = PERIODS.find(p => p.value === period)?.label || 'Range';
  // Colour the progress bar based on how the team's tracking:
  //   ≥100% green ✓ · 75-99% blue (almost there) · 50-74% amber · <50% rose
  const progressColor = targetPct >= 100 ? 'bg-emerald-500'
    : targetPct >= 75 ? 'bg-blue-500'
    : targetPct >= 50 ? 'bg-amber-500'
    : 'bg-rose-500';
  const progressText = targetPct >= 100 ? 'text-emerald-700'
    : targetPct >= 75 ? 'text-blue-700'
    : targetPct >= 50 ? 'text-amber-700'
    : 'text-rose-700';

  return (
    <div
      className="mb-4 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white shadow-sm"
      data-testid="jc-capacity-banner"
    >
      <div className="flex flex-wrap items-center gap-3 p-3">
        {/* iter178 — "Total Active Allocated Hours" tile — LIVE SNAPSHOT of
            quoted_labour_hours across ALL active job cards right now
            (Dropped-off → SM Review, incl. Break; EXCLUDES Completed /
            Booking / Quote). This tile does NOT respond to the
            Day/Week/Month toggle — it's the SM's true "outstanding
            workshop load right now" number. */}
        <div className="flex items-center gap-2 text-slate-700" data-testid="jc-capacity-allocated-tile">
          <div className="flex items-center justify-center h-9 w-9 rounded-full bg-indigo-100 text-indigo-700">
            <Clock className="h-4 w-4" />
          </div>
          <div>
            <p
              className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1"
              title="LIVE snapshot of quoted_labour_hours across ALL active job cards right now (Dropped-off, In Progress, Break, Waiting for Parts, Sublet, SM Review). Excludes Completed, Booking and Quote. Does NOT change with the Day/Week/Month filter — this is the outstanding work still in the workshop right now."
            >
              Total Active Allocated Hours
              <span className="text-[8px] font-bold text-indigo-600 bg-indigo-50 rounded px-1 py-px normal-case tracking-normal" data-testid="jc-capacity-allocated-live-badge">
                live
              </span>
            </p>
            <p className="text-2xl font-bold tabular-nums text-indigo-700" data-testid="jc-capacity-allocated">
              {loading ? '—' : fmtHours(snapshotAllocated)}
            </p>
          </div>
        </div>

        <div className="hidden md:block h-9 w-px bg-slate-200" />

        {/* iter198 — "Allocated Hours" tile — quoted_labour_hours for every
            TASK ADDED to a job card within the selected window (active +
            completed cards; excludes booking/quote). Buckets by when the task
            was allocated, NOT the card's scheduled/anchor day, so a reopen or
            reassignment never bumps old work into today. This is the SM's
            "labour we SOLD this period" number. Tap to audit the exact tasks. */}
        <button
          type="button"
          onClick={toggleAlloc}
          className="flex items-center gap-2 text-slate-700 text-left rounded-lg hover:bg-violet-50/60 transition-colors px-1 -mx-1"
          data-testid="jc-capacity-allocated-window-tile"
          title="Sum of quoted labour hours for every task ADDED to a job card during the selected period (active + completed cards; excludes booking & quote). Buckets by the day each task was allocated — a reopen or mechanic reassignment never moves work into today. This is 'how much labour we sold' for the period. Tap to audit the exact tasks."
        >
          <div className="flex items-center justify-center h-9 w-9 rounded-full bg-violet-100 text-violet-700">
            <Calendar className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
              Allocated Hours
              <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${allocExpanded ? 'rotate-180' : ''}`} />
            </p>
            <p className="text-2xl font-bold tabular-nums text-violet-700" data-testid="jc-capacity-allocated-window">
              {loading ? '—' : fmtHours(windowedAllocated)}
            </p>
            <p className="text-[9px] text-slate-400 leading-none">{periodLabel.toLowerCase()} · labour sold · tap to audit</p>
          </div>
        </button>

        <div className="hidden md:block h-9 w-px bg-slate-200" />

        <button
          type="button"
          onClick={toggleBilled}
          className="flex items-center gap-2 text-slate-700 text-left rounded-lg hover:bg-blue-50/60 transition-colors px-1 -mx-1"
          data-testid="jc-capacity-billed-tile"
          title="Sum of labour_hours across line_items on invoices dated in this window. Only invoiced job cards count. Tap to audit the exact invoices + lines."
        >
          <div className="flex items-center justify-center h-9 w-9 rounded-full bg-blue-100 text-blue-700">
            <Clock className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
              Billed Hours
              <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${billedExpanded ? 'rotate-180' : ''}`} />
            </p>
            <p className="text-2xl font-bold tabular-nums" data-testid="jc-capacity-total">
              {loading ? '—' : fmtHours(total)}
              {!loading && targetHours > 0 && (
                <span className="text-sm font-medium text-slate-400 ml-1">/ {fmtHours(targetHours)}</span>
              )}
            </p>
            <p className="text-[9px] text-slate-400 leading-none">{periodLabel.toLowerCase()} · invoiced labour · tap to audit</p>
          </div>
        </button>

        {/* iter125: Achieved vs Goal progress bar — shows the team's
            pacing for the active period. Tooltip explains the math. */}
        {!loading && targetHours > 0 && (
          <div
            className="hidden md:flex flex-col gap-1 min-w-[180px]"
            data-testid="jc-capacity-goal-progress"
            title={`${data?.active_mechanic_count || 0} mechanics × ${data?.weekly_target_per_mechanic || 38}h/wk × period`}
          >
            <div className="flex items-center justify-between text-[10px]">
              <span className="font-semibold uppercase tracking-wide text-slate-500">vs Goal</span>
              <span className={`font-bold ${progressText}`} data-testid="jc-capacity-goal-pct">
                {targetPct}%
              </span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${progressColor} transition-all`}
                style={{ width: `${Math.min(targetPct, 100)}%` }}
              />
            </div>
            <p className="text-[10px] text-slate-500 leading-tight">
              {targetPct >= 100
                ? `✓ Goal hit · +${fmtHours(total - targetHours)} over`
                : `${fmtHours(targetGap)} to go this ${periodLabel.toLowerCase().replace('this ', '')}`}
            </p>
          </div>
        )}

        <div className="text-sm text-slate-600 hidden md:block">
          <p className="font-medium">{jcCount} invoiced job card{jcCount === 1 ? '' : 's'}</p>
          <p className="text-xs text-slate-500">
            {periodLabel}{data?.start_date && data?.end_date && ` · ${data.start_date} → ${data.end_date}`}
          </p>
        </div>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {PERIODS.map(p => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${
                period === p.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`jc-capacity-period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
          {period === 'custom' && (
            <div className="flex items-center gap-1 bg-white border rounded-md px-2 h-8" data-testid="jc-capacity-custom-range">
              <Calendar className="h-3 w-3 text-slate-500" />
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="h-6 w-32 border-0 p-0 text-xs focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="jc-capacity-custom-start"
              />
              <span className="text-slate-400 text-xs">→</span>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="h-6 w-32 border-0 p-0 text-xs focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="jc-capacity-custom-end"
              />
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(v => !v)}
            className="text-xs h-7"
            data-testid="jc-capacity-toggle-expand"
          >
            <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            {expanded ? 'Hide breakdown' : 'Show breakdown'}
          </Button>
        </div>
      </div>

      {/* iter198 — "Allocated Hours" audit panel: exactly which tasks (and on
          which cards, added when) make up the labour-sold number. */}
      {allocExpanded && (
        <div className="border-t border-slate-100 p-3" data-testid="jc-capacity-allocated-breakdown">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-700">
              Allocated {periodLabel.toLowerCase()} · by task added date
            </p>
            <p className="text-[11px] text-slate-500 tabular-nums">
              {allocData ? `${fmtHours(allocData.total_allocated_hours)} · ${allocData.job_card_count} job card${allocData.job_card_count === 1 ? '' : 's'}` : ''}
            </p>
          </div>
          {allocLoading && !allocData ? (
            <p className="text-[11px] text-slate-400">Loading…</p>
          ) : !allocData || (allocData.job_cards || []).length === 0 ? (
            <p className="text-[11px] text-slate-400" data-testid="jc-capacity-allocated-breakdown-empty">
              No labour was allocated in this period. Tasks count here on the day the SM adds
              them to a dropped-off / in-progress job card.
            </p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-auto pr-1">
              {allocData.job_cards.map((j) => (
                <div
                  key={j.job_card_id}
                  className="border border-slate-100 rounded-lg p-2"
                  data-testid={`jc-capacity-alloc-jc-${j.job_card_id}`}
                >
                  <div className="flex items-center gap-2 text-xs mb-1">
                    <span className="font-semibold text-slate-700">{j.job_card_number || j.job_card_id}</span>
                    {j.vehicle_rego && <span className="text-slate-500">{j.vehicle_rego}</span>}
                    <span className="text-[9px] uppercase tracking-wide px-1.5 py-px rounded-full bg-slate-100 text-slate-500">
                      {(j.status || '').replace('_', ' ')}
                    </span>
                    <span className="ml-auto font-bold tabular-nums text-violet-700">{fmtHours(j.subtotal_hours)}</span>
                  </div>
                  <div className="space-y-0.5">
                    {j.tasks.map((t, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 text-[11px] text-slate-600"
                        data-testid={`jc-capacity-alloc-jc-${j.job_card_id}-task-${i}`}
                      >
                        <span className="w-9 shrink-0 text-right tabular-nums text-violet-600">{fmtHours(t.hours)}</span>
                        <span className="flex-1 truncate">{t.description}</span>
                        <span className="text-slate-400 shrink-0 tabular-nums">{(t.created_at || '').slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* iter220 — tasks touched in this window that DON'T count, with
              the exact reason (keyed another day / excluded status / deleted
              card) so "missing" hours are self-explanatory. */}
          {(allocData?.excluded_tasks || []).length > 0 && (
            <div className="mt-3 border-t border-amber-100 pt-2" data-testid="jc-capacity-alloc-excluded">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 mb-1">
                Not counted in this window · {fmtHours(allocData.excluded_total_hours)}
              </p>
              <div className="space-y-1 max-h-60 overflow-auto pr-1">
                {allocData.excluded_tasks.map((t, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-[11px]"
                    data-testid={`jc-capacity-alloc-excluded-row-${i}`}
                  >
                    <span className="w-9 shrink-0 text-right tabular-nums text-amber-700 font-medium">{fmtHours(t.hours)}</span>
                    {t.job_card_number && <span className="shrink-0 font-medium text-slate-600">{t.job_card_number}</span>}
                    <span className="flex-1 truncate text-slate-600">{t.description}</span>
                    <span className="shrink-0 text-amber-700">
                      {t.reason}{t.counts_on ? ` → counts on ${t.counts_on}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* iter223 — "Billed Hours" audit panel: each invoice (rego · invoice
          no · date header) with its hour-contributing lines, hours + amount
          charged ($0 when free), summing to the tile. */}
      {billedExpanded && (
        <div className="border-t border-slate-100 p-3" data-testid="jc-capacity-billed-breakdown">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">
              Billed {periodLabel.toLowerCase()} · by invoice
            </p>
            <p className="text-[11px] text-slate-500 tabular-nums">
              {billedData ? `${fmtHours(billedData.total_billed_hours)} · $${(billedData.total_amount || 0).toFixed(2)} · ${billedData.invoice_count} invoice${billedData.invoice_count === 1 ? '' : 's'}` : ''}
            </p>
          </div>
          {billedLoading && !billedData ? (
            <p className="text-[11px] text-slate-400">Loading…</p>
          ) : !billedData || (billedData.invoices || []).length === 0 ? (
            <p className="text-[11px] text-slate-400" data-testid="jc-capacity-billed-breakdown-empty">
              No labour was billed in this period. Lines count here on the day they
              landed on an invoice.
            </p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-auto pr-1">
              {billedData.invoices.map((inv) => (
                <div
                  key={inv.invoice_id}
                  className="border border-slate-100 rounded-lg p-2"
                  data-testid={`jc-capacity-billed-inv-${inv.invoice_id}`}
                >
                  {/* iter224 — header links straight to the invoice (new tab
                      so the SM keeps the kanban + audit context). */}
                  <a
                    href={withSessionHash(`/invoices/${inv.invoice_id}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs mb-1 rounded-md -mx-1 px-1 py-0.5 hover:bg-blue-50 transition-colors group/inv"
                    data-testid={`jc-capacity-billed-inv-link-${inv.invoice_id}`}
                    title={`Open ${inv.invoice_no}`}
                  >
                    {inv.vehicle_rego && <span className="font-semibold text-slate-800">{inv.vehicle_rego}</span>}
                    <span className="font-medium text-blue-700 underline-offset-2 group-hover/inv:underline">{inv.invoice_no}</span>
                    <ExternalLink className="h-3 w-3 text-slate-300 group-hover/inv:text-blue-600 transition-colors" />
                    <span className="text-slate-500 tabular-nums">{inv.date}</span>
                    {inv.customer_name && <span className="text-slate-400 truncate hidden sm:inline">· {inv.customer_name}</span>}
                    <span className="ml-auto font-bold tabular-nums text-blue-700">
                      {fmtHours(inv.subtotal_hours)}
                      <span className="text-slate-500 font-medium ml-2">${(inv.subtotal_amount || 0).toFixed(2)}</span>
                    </span>
                  </a>
                  <div className="space-y-0.5">
                    {inv.lines.map((l, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 text-[11px] text-slate-600"
                        data-testid={`jc-capacity-billed-inv-${inv.invoice_id}-line-${i}`}
                      >
                        <span className="w-9 shrink-0 text-right tabular-nums text-blue-600">{fmtHours(l.hours)}</span>
                        <span className="flex-1 truncate">{l.description}</span>
                        <span className="shrink-0 tabular-nums text-slate-700 font-medium">${(l.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {expanded && data && (
        <div className="border-t border-slate-100 p-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* By status */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-2">By stage</p>
            <div className="space-y-1">
              {['pickup', 'in_progress', 'break', 'waiting_for_parts', 'blocked', 'sm_review'].map(s => {
                const h = byStatus[s] || 0;
                if (h <= 0) return null;
                const pct = byStageTotal > 0 ? (h / byStageTotal) * 100 : 0;
                return (
                  <div key={s} className="flex items-center gap-2 text-xs" data-testid={`jc-capacity-status-${s}`}>
                    <span className="capitalize w-28 text-slate-600">{s.replace('_', ' ')}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-slate-700 font-medium tabular-nums w-12 text-right">{fmtHours(h)}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {/* By mechanic — iter144 shows produced / allocated side-by-side */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
              By mechanic{' '}
              <span className="font-normal text-slate-400">
                (<span className="text-indigo-700">allocated</span> /{' '}
                <span className="text-emerald-700">completed</span> /{' '}
                <span className="text-blue-700">produced</span>)
              </span>
            </p>
            <div className="space-y-1">
              {producedByMechanic.length === 0 && byMechanic.length === 0 && unassigned === 0 && (
                <p className="text-xs text-slate-400">No assignments yet for this range.</p>
              )}
              {/* iter212 — union of PRODUCED/ALLOCATED mechanics. The
                  indigo "allocated" figure comes ONLY from
                  produced_by_mechanic.allocated_hours (same window rule as
                  the Allocated Hours tile, so rows + unassigned = tile).
                  billed_by_mechanic rows are invoice-money attribution and
                  no longer override it. */}
              {(() => {
                const merged = {};
                producedByMechanic.forEach(m => {
                  merged[m.user_id] = {
                    user_id: m.user_id,
                    name: m.name,
                    produced: m.produced_hours || 0,
                    completed: m.completed_hours || 0,
                    allocated: m.allocated_hours || 0,
                    jc_count: m.jc_count || 0,
                    has_open: m.has_open_session,
                  };
                });
                if (producedByMechanic.length === 0) {
                  // Legacy fallback only (older API payloads).
                  byMechanic.forEach(m => {
                    merged[m.user_id] = {
                      user_id: m.user_id,
                      name: m.name,
                      produced: 0,
                      completed: 0,
                      allocated: m.hours || 0,
                      jc_count: m.jc_count || 0,
                      has_open: false,
                    };
                  });
                }
                const rows = Object.values(merged).sort((a, b) => b.produced - a.produced);
                return rows.map(m => {
                  const pct = m.allocated > 0 ? Math.min((m.produced / m.allocated) * 100, 100) : 0;
                  // Colour: green ≥90%, blue 60-89%, amber 30-59%, slate <30%
                  const barColor = pct >= 90 ? 'bg-emerald-500'
                                  : pct >= 60 ? 'bg-blue-500'
                                  : pct >= 30 ? 'bg-amber-500'
                                  : 'bg-slate-400';
                  const isOpen = expandedMech === m.user_id;
                  const bd = mechData[m.user_id];
                  return (
                    <div key={m.user_id}>
                      {/* iter193 — the whole row is a button; tapping it reveals
                          the completed-task audit list below. */}
                      <button
                        type="button"
                        onClick={() => toggleMech(m.user_id)}
                        className="w-full flex items-center gap-2 text-xs text-left rounded px-1 py-0.5 hover:bg-slate-50 transition-colors"
                        data-testid={`jc-capacity-mech-${m.user_id}`}
                        title="Tap to see the completed tasks behind these hours"
                      >
                        <ChevronDown className={`h-3 w-3 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        <span className="w-28 truncate text-slate-700 flex items-center gap-1">
                          {m.name}
                          {m.has_open && (
                            <span
                              className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"
                              title="Active clocked-in session"
                            />
                          )}
                        </span>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                        </div>
                        {/* iter163 — three-part hours display in requested
                            order: allocated / completed / produced. */}
                        <span
                          className="text-slate-700 font-medium tabular-nums w-36 text-right"
                          data-testid={`jc-capacity-mech-${m.user_id}-hours`}
                          title="Allocated (assigned work) / Completed (finished task hours) / Produced (clocked session time)"
                        >
                          <span className="text-indigo-700">{fmtHours(m.allocated)}</span>
                          <span className="text-slate-400 mx-0.5">/</span>
                          <span className="text-emerald-700">{fmtHours(m.completed)}</span>
                          <span className="text-slate-400 mx-0.5">/</span>
                          <span className="text-blue-700">{fmtHours(m.produced)}</span>
                        </span>
                        <span className="text-[10px] text-slate-400 w-8 text-right">{m.jc_count} jc</span>
                      </button>

                      {isOpen && (
                        <div
                          className="ml-5 mt-1 mb-2 border-l-2 border-emerald-100 pl-2 space-y-1"
                          data-testid={`jc-capacity-mech-${m.user_id}-breakdown`}
                        >
                          <p className="text-[9px] font-semibold uppercase tracking-wide text-emerald-700/70">
                            Completed tasks {periodLabel.toLowerCase()}
                          </p>
                          {mechLoading && !bd ? (
                            <p className="text-[11px] text-slate-400">Loading…</p>
                          ) : !bd || (bd.items || []).length === 0 ? (
                            <p className="text-[11px] text-slate-400" data-testid={`jc-capacity-mech-${m.user_id}-breakdown-empty`}>
                              No completed tasks in this range. A mechanic only earns completed
                              hours on job cards he actually clocked onto.
                            </p>
                          ) : (
                            <>
                              {bd.items.map((it, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-2 text-[11px]"
                                  data-testid={`jc-capacity-mech-${m.user_id}-task-${idx}`}
                                >
                                  <span className="text-emerald-700 font-semibold tabular-nums w-9 shrink-0 text-right">
                                    {fmtHours(it.hours)}
                                  </span>
                                  <span className="flex-1 text-slate-600 leading-tight">
                                    <span className="uppercase">{it.description}</span>
                                    <span className="text-slate-400">
                                      {' · '}{it.job_card_number || it.job_card_id}
                                      {it.vehicle_rego ? ` · ${it.vehicle_rego}` : ''}
                                      {it.kind === 'jc_fallback' ? ' · whole card' : ''}
                                    </span>
                                  </span>
                                </div>
                              ))}
                              <div className="flex items-center gap-2 text-[11px] pt-1 mt-1 border-t border-slate-100 font-semibold">
                                <span className="w-9 text-right text-emerald-800 tabular-nums" data-testid={`jc-capacity-mech-${m.user_id}-total`}>
                                  {fmtHours(bd.total_completed_hours)}
                                </span>
                                <span className="text-slate-500">total completed ({bd.items.length} task{bd.items.length === 1 ? '' : 's'})</span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
              {unassigned > 0 && (
                <div className="flex items-center gap-2 text-xs" data-testid="jc-capacity-mech-unassigned">
                  <span className="w-32 truncate text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Unassigned
                  </span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500" style={{ width: `${windowedAllocated > 0 ? (unassigned / windowedAllocated) * 100 : 0}%` }} />
                  </div>
                  <span className="text-amber-700 font-medium tabular-nums w-12 text-right">{fmtHours(unassigned)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
